/**
 * GuidebookTab — the "Guidebook" tab of Guest Experience (default tab).
 *
 * Handoff layout: a welcome-message card + a Publish & share card + an
 * "In this guidebook" completeness grid. Publish & share is fully wired to real
 * data (render/guest links, PIN-configured flag, QR via QrCodeSvg). The welcome
 * is AI-authored live per guest (no host-facing GET exists yet), so it's an
 * honest preview + "Preview live guidebook" action rather than a fabricated
 * quote. "Open guidebook studio" reveals the full GuidebookPreviewPage (live
 * iframe, PIN management, template, mobile/desktop preview).
 *
 * NOTE: welcome approve/publish/regenerate is net-new backend (deferred).
 */

import { useEffect, useMemo, useState } from "react"
import {
  Sparkles,
  Check,
  Copy,
  Download,
  ExternalLink,
  ArrowLeft,
  ArrowRight,
  KeyRound,
  DoorOpen,
  Home,
  MapPin,
  ScrollText,
  LogOut,
  Loader2,
  RefreshCw,
  type LucideIcon,
} from "lucide-react"
import { apiClient } from "@/lib/api"
import { usePropertyContext } from "@/lib/usePropertyContext"
import { cn } from "@/lib/utils"
import { QrCodeSvg, downloadQrSvg } from "@/components/QrCodeSvg"
import { GuidebookPreviewPage } from "@/pages/GuidebookPreviewPage"

const TEMPLATE_LABELS: Record<string, string> = { classic: "warm", concierge: "concierge", reserve: "reserve" }

interface Section {
  title: string
  note: string
  icon: LucideIcon
  ready: boolean
}

export function GuidebookTab() {
  const { propertyId, property } = usePropertyContext()
  const [renderKey, setRenderKey] = useState("")
  const [slug, setSlug] = useState("")
  const [pinConfigured, setPinConfigured] = useState(false)
  const [template, setTemplate] = useState("classic")
  const [placesCount, setPlacesCount] = useState(0)
  const [rulesReady, setRulesReady] = useState(false)
  const [checkoutReady, setCheckoutReady] = useState(false)
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState(false)
  const [editing, setEditing] = useState(false)

  // Welcome approval gate: AI drafts, the host approves before guests see it.
  const [welcomeMode, setWelcomeMode] = useState<"approved" | "adaptive" | null>(null)
  const [welcomeCopy, setWelcomeCopy] = useState<{ headline: string; message: string; hero_message: string } | null>(null)
  const [welcomeBusy, setWelcomeBusy] = useState<"" | "load" | "approve" | "regenerate" | "revert">("load")

  useEffect(() => {
    if (!propertyId) return
    let cancelled = false
    setWelcomeBusy("load")
    apiClient.getGuidebookWelcome(propertyId).then((res) => {
      if (cancelled) return
      if (res.ok) {
        setWelcomeMode(res.mode === "approved" ? "approved" : "adaptive")
        const copy = res.mode === "approved" && res.approved ? res.approved : res.draft
        if (copy?.message) {
          setWelcomeCopy({ headline: copy.headline || "Welcome", message: copy.message, hero_message: copy.hero_message || "" })
        }
      }
    }).catch(() => {}).finally(() => !cancelled && setWelcomeBusy(""))
    return () => {
      cancelled = true
    }
  }, [propertyId])

  const approveWelcome = async () => {
    if (!propertyId || !welcomeCopy || welcomeBusy) return
    setWelcomeBusy("approve")
    try {
      const res = await apiClient.approveGuidebookWelcome(propertyId, welcomeCopy)
      if (res.ok) setWelcomeMode("approved")
    } catch { /* stay adaptive */ } finally {
      setWelcomeBusy("")
    }
  }

  const regenerateWelcome = async () => {
    if (!propertyId || welcomeBusy) return
    setWelcomeBusy("regenerate")
    try {
      const res = await apiClient.regenerateGuidebookWelcome(propertyId)
      if (res.ok && res.draft?.message) {
        setWelcomeCopy({ headline: res.draft.headline || "Welcome", message: res.draft.message, hero_message: res.draft.hero_message || "" })
        setWelcomeMode("adaptive")
      }
    } catch { /* keep current */ } finally {
      setWelcomeBusy("")
    }
  }

  const revertWelcome = async () => {
    if (!propertyId || welcomeBusy) return
    setWelcomeBusy("revert")
    try {
      const res = await apiClient.revertGuidebookWelcome(propertyId)
      if (res.ok) setWelcomeMode("adaptive")
    } catch { /* keep approved */ } finally {
      setWelcomeBusy("")
    }
  }

  useEffect(() => {
    if (!propertyId) return
    let cancelled = false
    setLoading(true)
    Promise.allSettled([
      apiClient.getPropertySettings(propertyId),
      apiClient.getPlaces(propertyId),
      apiClient.getHouseRules(propertyId),
    ]).then(([settingsRes, placesRes, rulesRes]) => {
      if (cancelled) return
      if (settingsRes.status === "fulfilled") {
        const s = (settingsRes.value as { settings?: Record<string, unknown> }).settings || {}
        const prop = (s.property as Record<string, unknown>) || {}
        const cfg = (s.property_config as Record<string, unknown>) || {}
        setRenderKey(String(s.render_key || prop.render_key || ""))
        setSlug(String(prop.slug || "").trim().toLowerCase())
        setPinConfigured(Boolean(cfg.guidebook_pin_configured))
        setTemplate(String(cfg.guidebook_template || "classic"))
      }
      if (placesRes.status === "fulfilled" && Array.isArray(placesRes.value.places)) {
        setPlacesCount(placesRes.value.places.length)
      }
      if (rulesRes.status === "fulfilled" && rulesRes.value.rules) {
        const r = rulesRes.value.rules as Record<string, Record<string, unknown>>
        setRulesReady(["quietHours", "smoking", "pets", "parties", "visitors"].some((k) => r[k]?.enabled === true))
        setCheckoutReady(r.checkout?.enabled === true)
      }
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [propertyId])

  const directUrl = renderKey ? `${window.location.origin}/g/${renderKey}` : null
  const guestUrl = slug ? `${window.location.origin}/p/${slug}` : null

  const sections: Section[] = useMemo(
    () => [
      { title: "Welcome", note: "Personal note in your voice", icon: Sparkles, ready: true },
      { title: "Getting in", note: "Door code, parking, access", icon: DoorOpen, ready: true },
      { title: "The space", note: "Wi-Fi, heating, the essentials", icon: Home, ready: true },
      { title: "Local picks", note: placesCount ? `${placesCount} places curated nearby` : "Add local recommendations", icon: MapPin, ready: placesCount > 0 },
      { title: "House rules", note: "Quiet hours, smoking, checkout", icon: ScrollText, ready: rulesReady },
      { title: "Checkout", note: checkoutReady ? "Checkout steps set" : "Add your checkout steps", icon: LogOut, ready: checkoutReady },
    ],
    [placesCount, rulesReady, checkoutReady],
  )
  const readyCount = sections.filter((s) => s.ready).length

  const copyGuestLink = () => {
    if (!guestUrl) return
    navigator.clipboard.writeText(guestUrl).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }).catch(() => {})
  }

  if (editing) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="mb-4 inline-flex items-center gap-1.5 rounded-[9px] border border-border bg-card px-3 py-2 text-[12.5px] font-semibold text-ink-2 transition-colors hover:bg-line-soft"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
          Back to guidebook
        </button>
        <GuidebookPreviewPage />
      </div>
    )
  }

  return (
    <div>
      <section className="mb-[18px] grid grid-cols-1 gap-[18px] lg:grid-cols-[1.45fr_1fr]">
        {/* Welcome message */}
        <div className="rounded-card border border-border bg-card p-[22px] shadow-sm">
          <div className="mb-3.5 flex items-center justify-between gap-2.5">
            <h3 className="font-display text-[16px] font-bold tracking-[-0.01em] text-foreground">Welcome message</h3>
            {welcomeMode === "approved" ? (
              <span className="inline-flex flex-none items-center gap-1.5 rounded-full bg-brand px-2.5 py-[3px] text-[11px] font-semibold text-white">
                <Check className="h-[11px] w-[11px]" strokeWidth={2.4} />
                Approved · live to guests
              </span>
            ) : (
              <span className="inline-flex flex-none items-center gap-1.5 rounded-full bg-brand-soft px-2.5 py-[3px] text-[11px] font-semibold text-brand-strong">
                <Sparkles className="h-[11px] w-[11px] fill-current" strokeWidth={0} />
                AI · {TEMPLATE_LABELS[template] || "warm"} voice · adaptive
              </span>
            )}
          </div>
          <div className="rounded-[13px] border border-border bg-line-soft p-5 font-serif text-[15.5px] italic leading-[1.68] text-ink-2">
            {welcomeBusy === "load" ? (
              <span className="inline-flex items-center gap-2 not-italic font-sans text-[13px] text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
                Fetching your welcome…
              </span>
            ) : welcomeCopy ? (
              `“${welcomeCopy.message}”`
            ) : (
              "Your guidebook opens with an AI-written welcome that greets each guest by name and adapts to the weather and the moment in their stay — rendered live across the phone, the in-room display, and the TV."
            )}
          </div>
          {welcomeMode === "adaptive" && welcomeCopy && (
            <p className="mt-2 text-[11.5px] font-medium text-muted-foreground">
              Adaptive mode: AI re-writes this live per guest and weather. Approve it to lock this exact copy on every surface.
            </p>
          )}
          <div className="mt-4 flex flex-wrap gap-2.5">
            {welcomeMode === "adaptive" && welcomeCopy && (
              <button
                type="button"
                onClick={approveWelcome}
                disabled={welcomeBusy !== ""}
                className="inline-flex items-center gap-1.5 rounded-[10px] bg-forest px-4 py-2.5 text-[13px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                {welcomeBusy === "approve" ? <Loader2 className="h-[14px] w-[14px] animate-spin" strokeWidth={2.2} /> : <ArrowRight className="h-[14px] w-[14px]" strokeWidth={2.2} />}
                Approve &amp; publish
              </button>
            )}
            {welcomeMode === "approved" && (
              <button
                type="button"
                onClick={revertWelcome}
                disabled={welcomeBusy !== ""}
                className="inline-flex items-center gap-1.5 rounded-[10px] border border-border bg-card px-4 py-2.5 text-[13px] font-semibold text-foreground transition-colors hover:bg-line-soft disabled:opacity-60"
              >
                {welcomeBusy === "revert" ? <Loader2 className="h-[14px] w-[14px] animate-spin" strokeWidth={2.2} /> : null}
                Revert to adaptive
              </button>
            )}
            {welcomeMode !== null && (
              <button
                type="button"
                onClick={regenerateWelcome}
                disabled={welcomeBusy !== ""}
                className="inline-flex items-center gap-1.5 rounded-[10px] border border-border bg-card px-4 py-2.5 text-[13px] font-semibold text-foreground transition-colors hover:bg-line-soft disabled:opacity-60"
              >
                {welcomeBusy === "regenerate" ? <Loader2 className="h-[14px] w-[14px] animate-spin" strokeWidth={2.2} /> : <RefreshCw className="h-[14px] w-[14px]" strokeWidth={2} />}
                Regenerate
              </button>
            )}
            <a
              href={directUrl || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "inline-flex items-center gap-1.5 rounded-[10px] border border-border bg-card px-4 py-2.5 text-[13px] font-semibold text-foreground transition-colors hover:bg-line-soft",
                !directUrl && "pointer-events-none opacity-50",
              )}
            >
              <ExternalLink className="h-[14px] w-[14px]" strokeWidth={2} />
              Preview live
            </a>
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1.5 rounded-[10px] border border-border bg-card px-4 py-2.5 text-[13px] font-semibold text-foreground transition-colors hover:bg-line-soft"
            >
              Guidebook studio
            </button>
          </div>
        </div>

        {/* Publish & share */}
        <div className="rounded-card border border-border bg-card p-[22px] shadow-sm">
          <div className="mb-3.5 text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">
            Publish &amp; share
          </div>
          <div className="mb-4 flex items-center gap-3.5">
            <div className="flex h-[76px] w-[76px] flex-none items-center justify-center rounded-xl border border-border bg-white p-1.5">
              {guestUrl ? <QrCodeSvg value={guestUrl} size={64} /> : <div className="h-full w-full rounded bg-line-soft" />}
            </div>
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-foreground" style={{ fontWeight: 650 }}>
                <span className={cn("h-[7px] w-[7px] rounded-full", guestUrl ? "bg-brand" : "bg-muted-foreground")} />
                {guestUrl ? "Guidebook is live" : "Not published yet"}
              </div>
              <div className="mt-1 truncate font-mono text-[12px] text-muted-foreground">
                {guestUrl ? guestUrl.replace(/^https?:\/\//, "") : "—"}
              </div>
              {pinConfigured && (
                <div className="mt-1.5 inline-flex items-center gap-1 text-[11.5px] font-medium text-brand-strong">
                  <KeyRound className="h-3 w-3" strokeWidth={2} />
                  PIN protected
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2.5">
            <button
              type="button"
              onClick={copyGuestLink}
              disabled={!guestUrl}
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-[9px] border border-border bg-card py-2.5 text-[12.5px] font-semibold text-foreground transition-colors hover:bg-line-soft disabled:opacity-50"
            >
              {copied ? <Check className="h-[13px] w-[13px]" strokeWidth={2.2} /> : <Copy className="h-[13px] w-[13px]" strokeWidth={1.9} />}
              {copied ? "Copied" : "Copy link"}
            </button>
            <button
              type="button"
              onClick={() => guestUrl && downloadQrSvg(guestUrl, slug || "guidebook")}
              disabled={!guestUrl}
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-[9px] border border-border bg-card py-2.5 text-[12.5px] font-semibold text-foreground transition-colors hover:bg-line-soft disabled:opacity-50"
            >
              <Download className="h-[13px] w-[13px]" strokeWidth={1.9} />
              Download QR
            </button>
          </div>
        </div>
      </section>

      {/* In this guidebook */}
      <section className="rounded-card border border-border bg-card p-[22px] shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-[16px] font-bold tracking-[-0.01em] text-foreground">In this guidebook</h3>
          <span className="text-[12px] font-semibold text-brand-strong">{loading ? "—" : `${readyCount} of 6 ready`}</span>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {sections.map((sec) => (
            <article
              key={sec.title}
              className={cn(
                "rounded-[13px] border p-[15px]",
                sec.ready ? "border-border bg-card" : "border-amber bg-[linear-gradient(180deg,var(--sf-amber-soft),var(--sf-card)_70%)]",
              )}
            >
              <div className="mb-2 flex items-start justify-between">
                <span className={cn("flex h-[30px] w-[30px] items-center justify-center rounded-[9px]", sec.ready ? "bg-brand-soft" : "bg-line-soft")}>
                  <sec.icon className={cn("h-[15px] w-[15px]", sec.ready ? "text-brand-strong" : "text-muted-foreground")} strokeWidth={1.8} />
                </span>
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.03em]",
                    sec.ready ? "bg-brand-soft text-brand-strong" : "bg-amber-soft text-amber-strong",
                  )}
                >
                  {sec.ready ? "Ready" : "To do"}
                </span>
              </div>
              <h4 className="text-[14px] font-semibold text-foreground" style={{ fontWeight: 650 }}>{sec.title}</h4>
              <p className="mt-1 text-[12px] leading-[1.45] text-muted-foreground">{sec.note}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  )
}
